import React, { useState, useEffect } from 'react';
import transactionService from '../services/transactionService';
import Card from '../components/common/Card';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', account_id: '' });

  useEffect(() => {
    fetchTransactions();
  }, [filter]);

  const fetchTransactions = async () => {
    try {
      const params = {};
      if (filter.type) params.type = filter.type;
      if (filter.account_id) params.account_id = filter.account_id;

      const data = await transactionService.getTransactions(params);
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // REMOVE OLD formatCurrency function - we'll use formatted_amount from backend
  // const formatCurrency = (amount) => {
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //   }).format(amount);
  // };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // NEW: Get transaction description based on direction
  const getTransactionDescription = (txn) => {
    if (txn.description) return txn.description;
    
    if (txn.direction === 'incoming') {
      return `From: ${txn.from_account_number || 'External Account'}`;
    } else if (txn.direction === 'outgoing') {
      return `To: ${txn.to_account_number || 'External Account'}`;
    }
    
    return 'Transaction';
  };

  // NEW: Get account number to display
  const getDisplayAccount = (txn) => {
    if (txn.direction === 'incoming') {
      return txn.from_account_number || 'External';
    } else if (txn.direction === 'outgoing') {
      return txn.to_account_number || 'External';
    }
    return txn.from_account_number || txn.to_account_number || '--';
  };

  // NEW: Get amount color based on direction
  const getAmountColor = (txn) => {
    if (txn.direction === 'incoming' || txn.transaction_type === 'DEPOSIT') {
      return 'text-success-600'; // Green for incoming/deposits
    } else {
      return 'text-accent-600'; // Red for outgoing/withdrawals
    }
  };

  // NEW: Get transaction type display name
  const getTransactionTypeDisplay = (txn) => {
    const types = {
      'TRANSFER': 'Transfer',
      'DEPOSIT': 'Deposit',
      'WITHDRAWAL': 'Withdrawal',
      'PAYMENT': 'Payment',
      'INTERNATIONAL': 'International'
    };
    return types[txn.transaction_type] || txn.transaction_type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">Transaction History</h1>
          <p className="text-neutral-600">Track and manage all your financial activities</p>
        </div>

        {/* Filters */}
        <Card className="mb-8 p-6 bg-white rounded-2xl shadow-lg border border-neutral-100">
          <div className="flex flex-wrap gap-6 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Filter by Type
              </label>
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white text-neutral-700"
              >
                <option value="">All Types</option>
                <option value="TRANSFER">Transfer</option>
                <option value="DEPOSIT">Deposit</option>
                <option value="WITHDRAWAL">Withdrawal</option>
                <option value="PAYMENT">Payment</option>
                <option value="INTERNATIONAL">International</option>
              </select>
            </div>
            
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Account Filter
              </label>
              <select
                value={filter.account_id}
                onChange={(e) => setFilter({ ...filter, account_id: e.target.value })}
                className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white text-neutral-700"
              >
                <option value="">All Accounts</option>
                {/* Account options would be populated dynamically */}
              </select>
            </div>

            <button
              onClick={() => setFilter({ type: '', account_id: '' })}
              className="px-6 py-3 bg-neutral-100 text-neutral-700 rounded-xl font-semibold hover:bg-neutral-200 transition transform hover:scale-105"
            >
              Clear Filters
            </button>
          </div>
        </Card>

        {/* Transactions List */}
        <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 overflow-hidden">
          {transactions.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-12 h-12 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">No transactions found</h3>
              <p className="text-neutral-600">Try adjusting your filters or make your first transaction</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                      From/To
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {transactions.map((txn) => (
                    <tr 
                      key={txn.id} 
                      className="hover:bg-primary-50 transition duration-150 ease-in-out group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-neutral-900 group-hover:text-primary-700">
                          {formatDate(txn.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          txn.transaction_type === 'DEPOSIT' ? 'bg-success-100 text-success-700' :
                          txn.transaction_type === 'WITHDRAWAL' ? 'bg-accent-100 text-accent-700' :
                          txn.transaction_type === 'TRANSFER' ? 'bg-primary-100 text-primary-700' :
                          txn.transaction_type === 'INTERNATIONAL' ? 'bg-secondary-100 text-secondary-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {getTransactionTypeDisplay(txn)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-neutral-600 group-hover:text-neutral-800">
                          {getDisplayAccount(txn)}
                        </div>
                        <div className="text-xs text-neutral-400">
                          {txn.direction === 'incoming' ? 'From' : 'To'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-neutral-600 group-hover:text-neutral-800 max-w-xs truncate">
                          {getTransactionDescription(txn)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${getAmountColor(txn)} group-hover:scale-105 transition-transform`}>
                          {/* Use formatted_amount from backend */}
                          {txn.formatted_amount || `$${txn.amount}`}
                        </div>
                        <div className="text-xs text-neutral-400">
                          {txn.direction === 'incoming' ? 'Incoming' : 'Outgoing'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          txn.status === 'COMPLETED' ? 'bg-success-100 text-success-700' :
                          txn.status === 'PENDING' ? 'bg-secondary-100 text-secondary-700' :
                          'bg-accent-100 text-accent-700'
                        }`}>
                          {txn.status === 'COMPLETED' && (
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {txn.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Summary Stats */}
        {transactions.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-xl shadow border border-neutral-100">
              <div className="text-sm text-neutral-600">Total Transactions</div>
              <div className="text-2xl font-bold text-neutral-900">{transactions.length}</div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow border border-neutral-100">
              <div className="text-sm text-neutral-600">Total Volume</div>
              <div className="text-2xl font-bold text-success-600">
                {/* Calculate total volume (absolute values) */}
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(transactions.reduce((sum, txn) => sum + Math.abs(parseFloat(txn.amount)), 0))}
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow border border-neutral-100">
              <div className="text-sm text-neutral-600">Completed</div>
              <div className="text-2xl font-bold text-primary-600">
                {transactions.filter(txn => txn.status === 'COMPLETED').length}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transactions;